// Menu por categorias com nomes (computador), busca de funções (Ctrl+K) e telas de categoria do celular.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Widget } from './Widget';
import { ALL_ITEMS, MENU, loadFavs, saveFavs, searchMenu, type MenuCat, type MenuItem } from './menu';

function useFavs(): [string[], (key: string) => void] {
  const [favs, setFavs] = useState<string[]>(loadFavs);
  const toggle = (key: string) => setFavs((f) => { const n = f.includes(key) ? f.filter((k) => k !== key) : [...f, key].slice(-8); saveFavs(n); return n; });
  return [favs, toggle];
}
const byKey = (k: string) => ALL_ITEMS.find((i) => i.key === k);

function Ico(props: { src?: string }) {
  return <span class="mn-ico">{props.src ? <img src={props.src} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} /> : null}</span>;
}

/** Computador: substitui a grade de ícones do painel da direita. Minimizável (botão –), lista com altura ajustável
 * (arrastar a borda de baixo) e clicar de novo na aba aberta recolhe a lista; tudo fica salvo no navegador. */
export function MenuPanel(props: { onPick: (it: MenuItem) => void; onSearch: () => void }) {
  const ls = (k: string, d: string) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
  const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sem storage */ } };
  const [cat, setCat] = useState<MenuCat['id'] | ''>(() => ls('nx-menu-cat', 'caca') as MenuCat['id'] | '');
  const [h, setH] = useState(() => Number(ls('nx-menu-h', '0')) || 0);   // 0 = altura natural
  const [favs, toggleFav] = useFavs();
  const pick = (id: MenuCat['id']) => { const v = id === cat ? '' : id; setCat(v); lsSet('nx-menu-cat', v); };
  const c = MENU.find((m) => m.id === cat);
  const list = useRef<HTMLDivElement>(null);
  const startDrag = (e: PointerEvent) => {
    e.preventDefault();
    const y0 = e.clientY, h0 = list.current?.offsetHeight ?? 100;
    let last = h0;
    const move = (ev: PointerEvent) => { last = Math.max(44, Math.min(600, h0 + ev.clientY - y0)); setH(last); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); lsSet('nx-menu-h', String(Math.round(last))); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  return (
    <Widget id="menu" title="Menu" closable={false}>
      <div class="mn-panel">
        <button class="mn-search" onClick={props.onSearch} title="Procurar qualquer função pelo nome (atalho: Ctrl+K)">🔍 Buscar função… <kbd>Ctrl+K</kbd></button>
        {favs.length > 0 && (
          <div class="mn-favs" title="Fixados: clique na estrela ☆ de qualquer item para fixar ou tirar daqui">
            {favs.map((k) => { const it = byKey(k); return it ? <button key={k} class="mn-fav" title={it.desc || it.label} onClick={() => props.onPick(it)}><Ico src={it.icon} /><span>{it.label}</span></button> : null; })}
          </div>
        )}
        <div class="mn-tabs">
          {MENU.map((m) => <button key={m.id} class={'mn-tab' + (m.id === cat ? ' on' : '')} title={m.id === cat ? 'Clique de novo para recolher' : ''} onClick={() => pick(m.id)}>{m.label}</button>)}
        </div>
        {c && (
          <>
            <div class="mn-list" ref={list} style={h ? { height: h + 'px' } : undefined}>
              {c.items.map((it) => (
                <div key={it.key} class="mn-row">
                  <button class="mn-item" title={it.desc || it.label} onClick={() => props.onPick(it)}><Ico src={it.icon} /><span>{it.label}</span></button>
                  <button class={'mn-star' + (favs.includes(it.key) ? ' on' : '')} title={favs.includes(it.key) ? 'Tirar dos fixados' : 'Fixar no topo'} onClick={() => toggleFav(it.key)}>{favs.includes(it.key) ? '★' : '☆'}</button>
                </div>
              ))}
            </div>
            <div class="bl-resize" title="Arraste para aumentar ou diminuir a lista (duplo clique volta ao tamanho normal)" onPointerDown={startDrag as any} onDblClick={() => { setH(0); lsSet('nx-menu-h', '0'); }} />
          </>
        )}
      </div>
    </Widget>
  );
}

/** Busca de funções: digite parte do nome (sem precisar de acento) e Enter abre */
export function CommandPalette(props: { onPick: (it: MenuItem) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); }, []);
  const res = useMemo(() => (q.trim() ? searchMenu(q) : ALL_ITEMS), [q]);
  useEffect(() => setSel(0), [q]);
  const go = (it?: MenuItem) => { if (!it) return; props.onClose(); props.onPick(it); };
  return (
    <div class="cp-bg" onClick={props.onClose}>
      <div class="cp-box" onClick={(e) => e.stopPropagation()}>
        <input ref={input} class="cp-input" placeholder="Buscar função: hunts, forge, refine, depot, prey…" value={q}
          onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); props.onClose(); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(res.length - 1, s + 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
            else if (e.key === 'Enter') { e.preventDefault(); go(res[sel]); }
          }} />
        <div class="cp-list">
          {res.length === 0 && <p class="cp-empty">Nada encontrado com "{q}".</p>}
          {res.map((it, i) => {
            const cat = MENU.find((m) => m.items.includes(it))?.label;
            return (
              <button key={it.key} class={'cp-item' + (i === sel ? ' on' : '')} onMouseEnter={() => setSel(i)} onClick={() => go(it)}>
                <Ico src={it.icon} />
                <span class="cp-txt"><b>{it.label}</b>{it.desc ? <em>{it.desc}</em> : null}</span>
                <i class="cp-cat">{cat}</i>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export interface SheetExtra { key: string; label: string; desc?: string; icon?: string; on?: boolean; disabled?: boolean; onClick: () => void }

/** Celular: tela cheia de uma aba (Caça, Personagem, Itens, Menu) com botões grandes e nomes */
export function MobileSheet(props: { title: string; sections: Array<{ label?: string; extra?: SheetExtra[]; items?: MenuItem[] }>; search?: boolean; onPick: (it: MenuItem) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const found = props.search && q.trim() ? searchMenu(q) : null;
  const btn = (key: string, label: string, desc: string | undefined, icon: string | undefined, onClick: () => void, on?: boolean, disabled?: boolean) => (
    <button key={key} class={'ms-btn' + (on ? ' on' : '')} disabled={disabled} onClick={onClick}>
      <Ico src={icon} /><span class="ms-txt"><b>{label}</b>{desc ? <em>{desc}</em> : null}</span>
    </button>
  );
  return (
    <div class="ms-sheet">
      <div class="ms-head"><button class="ms-back" onClick={props.onClose}>‹ Voltar ao jogo</button><b>{props.title}</b></div>
      {props.search && <input class="ms-search" placeholder="🔍 Buscar função (forge, refine, prey…)" value={q} onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)} />}
      <div class="ms-body">
        {found ? (
          <div class="ms-grid">{found.length ? found.map((it) => btn(it.key, it.label, it.desc, it.icon, () => props.onPick(it))) : <p class="cp-empty">Nada encontrado com "{q}".</p>}</div>
        ) : props.sections.map((s, i) => (
          <section key={i}>
            {s.label && <h4 class="ms-sec">{s.label}</h4>}
            <div class="ms-grid">
              {(s.extra ?? []).map((x) => btn(x.key, x.label, x.desc, x.icon, x.onClick, x.on, x.disabled))}
              {(s.items ?? []).map((it) => btn(it.key, it.label, it.desc, it.icon, () => props.onPick(it)))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export type MobileTab = 'jogo' | 'caca' | 'personagem' | 'itens' | 'menu';
/** Celular: abas fixas embaixo */
export function MobileTabs(props: { tab: MobileTab; onTab: (t: MobileTab) => void }) {
  const tabs: Array<[MobileTab, string, string]> = [['jogo', '🎮', 'Jogo'], ['caca', '⚔️', 'Caça'], ['personagem', '🧍', 'Personagem'], ['itens', '🎒', 'Itens'], ['menu', '☰', 'Menu']];
  return (
    <nav class="m-tabs">
      {tabs.map(([t, ico, label]) => <button key={t} class={'m-tab' + (props.tab === t ? ' on' : '')} onClick={() => props.onTab(t)}><i>{ico}</i><span>{label}</span></button>)}
    </nav>
  );
}
